/**
 * Pure decision logic for the Akhara time heartbeat. A person counts as active in
 * a 5-minute window when the tab is visible and they did something: input in the
 * last 5 minutes, or input in the last 30 minutes while a video plays or focus is
 * inside an embedded player iframe (the Bunny films).
 */
export const WINDOW_SEC = 300;
export const WINDOW_MS = WINDOW_SEC * 1000;
export const ACTIVE_MS = 5 * 60 * 1000;
export const MEDIA_ACTIVE_MS = 30 * 60 * 1000;

export interface ActivityState {
  /** ms epoch of the last user input; null when none yet. */
  lastInputAt: number | null;
  visible: boolean;
  /** A <video> is playing, or focus is inside an iframe. */
  mediaActive: boolean;
}

/** The 5-minute window index for a ms epoch, same as the server's floor(epochSeconds / 300). */
export function windowOf(ms: number): number {
  return Math.floor(ms / WINDOW_MS);
}

export function isActive(now: number, state: ActivityState): boolean {
  if (!state.visible || state.lastInputAt == null) return false;
  const idle = now - state.lastInputAt;
  if (idle < 0) return true;
  return idle <= (state.mediaActive ? MEDIA_ACTIVE_MS : ACTIVE_MS);
}

/** True when a heartbeat should go out now: active, and this window not yet sent. */
export function shouldSend(now: number, state: ActivityState, lastSentWindow: number | null): boolean {
  return isActive(now, state) && lastSentWindow !== windowOf(now);
}
